import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import ExtensionHealthPanel from "@/components/ExtensionHealthPanel";
import {
  Users, House, Image as ImageIcon, EnvelopeSimple, CheckCircle,
  TrendUp, Lock, Eye, Phone, Heartbeat, Warning, Pulse, Database, BugBeetle,
  Sparkle,
} from "@phosphor-icons/react";

function StatCard({ label, value, sub, icon: Icon, accent = false, testid }) {
  return (
    <div
      className={`p-5 border ${accent ? "bg-[#F1F4ED] border-[#7C8B6E]/30" : "bg-white border-[#E5E7E0]"}`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10.5px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold">{label}</div>
        {Icon && <Icon size={16} weight="duotone" className={accent ? "text-[#7C8B6E]" : "text-[#9A968B]"} />}
      </div>
      <div className={`mt-2 font-editorial tracking-tight ${accent ? "text-[#1C1C1A]" : "text-[#1C1C1A]"}`} style={{ fontSize: 36, lineHeight: 1 }}>
        {value}
      </div>
      {sub && <div className="text-[11px] text-[#9A968B] mt-1.5">{sub}</div>}
    </div>
  );
}

/** Mini-sparkline 14gg signup trend */
function Sparkline({ trend }) {
  if (!trend || trend.length === 0) return null;
  const max = Math.max(...trend.map((t) => t.count), 1);
  const W = 380, H = 60;
  const dx = W / (trend.length - 1 || 1);
  const points = trend.map((t, i) => `${i * dx},${H - (t.count / max) * (H - 4) - 2}`).join(" ");
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="overflow-visible">
      <polyline fill="none" stroke="#7C8B6E" strokeWidth="2" points={points} />
      {trend.map((t, i) => (
        <circle key={i} cx={i * dx} cy={H - (t.count / max) * (H - 4) - 2} r={t.count > 0 ? 3 : 1.5} fill="#7C8B6E" />
      ))}
    </svg>
  );
}

export default function Admin() {
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [statsRes, healthRes] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/health").catch(() => ({ data: null })),
        ]);
        setData(statsRes.data);
        setHealth(healthRes.data);
      } catch (e) {
        if (e?.response?.status === 403) setForbidden(true);
      } finally { setLoading(false); }
    })();
  }, []);

  const sortedUsers = useMemo(() => data?.recent_users || [], [data]);

  if (loading) {
    return <AppShell title="Admin"><div className="text-[#6B6A65]">Caricamento…</div></AppShell>;
  }
  if (forbidden) {
    return (
      <AppShell title="Admin">
        <div className="max-w-xl border border-[#E5E7E0] bg-white p-8 text-center" data-testid="admin-forbidden">
          <Lock size={36} weight="duotone" className="mx-auto text-[#9A968B] mb-3" />
          <h2 className="font-editorial text-2xl text-[#1C1C1A]">Console riservata</h2>
          <p className="mt-2 text-[#6B6A65] text-sm">
            Questa è la console del team Pronto. La tua email non è nell'allowlist.
          </p>
        </div>
      </AppShell>
    );
  }

  const s = data?.summary || {};
  const KIND_LABELS = {
    verification: "Conferma email",
    lead_notification: "Notifica lead all'agente",
    lead_auto_reply: "Risposta automatica al lead",
    viewing_confirmation: "Conferma sopralluogo",
    password_reset: "Reset password",
    marketplace_new_listing: "Marketplace · nuovo annuncio",
    transactional: "Altro transazionale",
    unknown: "Senza categoria",
  };
  const quotaTone = (st) => st === "critical"
    ? { bg: "#FCE9E4", border: "#F2B6A4", text: "#8A2914", bar: "#C0392B" }
    : st === "warning"
    ? { bg: "#FFF4E5", border: "#F2D9B0", text: "#7A4E0E", bar: "#D98E1E" }
    : { bg: "#EFF4E9", border: "#CBD9B8", text: "#3F4A33", bar: "#7C8B6E" };

  return (
    <AppShell title="Admin" breadcrumb="Console founder">
      <div className="mb-6">
        <p className="text-[10.5px] uppercase tracking-[0.2em] text-[#6B6A65] mb-1.5">Console founder · live</p>
        <h1 className="font-editorial text-4xl lg:text-5xl tracking-tighter leading-[1.05] text-[#1C1C1A]" data-testid="admin-title">
          Salute app · oggi
        </h1>
        <p className="mt-2 text-[13px] text-[#6B6A65]">
          Tutto quello che ti serve per controllare ogni mattina che Pronto giri bene: email Resend, errori client, soft-ban, iscrizioni.
        </p>
      </div>

      {/* iter v28 r404 — Esporta snapshot per dev (banner sticky in cima) */}
      <ExportSnapshotBanner />

      {/* iter v28 r465 — Ricarica crediti utente per email */}
      <GrantCreditsPanel />

      {/* iter v28 r404 — DAU (Daily Active Users): chi USA davvero l'app */}
      <DauPanel />

      {/* iter v28 r404 — Errori server (5xx) catturati dal middleware */}
      <ServerErrorsPanel />

      {/* Errori client — early warning system (iter 267) */}
      <ClientErrorsPanel />

      {/* iter v27 r4 — Pannello SALUTE APP · founder daily check-up */}
      {health && (
        <div className="mb-10 mt-10" data-testid="health-panel">
          {/* Riga 1: KPI principali */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
            {/* Quota Resend */}
            <div
              className="p-4 border rounded"
              style={(() => { const t = quotaTone(health.email.quota_status); return { background: t.bg, borderColor: t.border, color: t.text }; })()}
              data-testid="health-email-quota"
            >
              <div className="flex items-center justify-between">
                <div className="text-[10.5px] uppercase tracking-[0.2em] font-semibold">Quota email oggi</div>
                <EnvelopeSimple size={16} weight="duotone" />
              </div>
              <div className="mt-2 font-editorial tracking-tight" style={{ fontSize: 32, lineHeight: 1 }}>
                {health.email.today_sent}<span className="text-[14px] opacity-70"> / {health.email.daily_quota}</span>
              </div>
              <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.6)" }}>
                <div className="h-full" style={{ width: `${Math.min(100, health.email.quota_pct)}%`, background: quotaTone(health.email.quota_status).bar }} />
              </div>
              <div className="text-[11px] mt-1.5 opacity-90">
                Ieri: {health.email.yesterday_sent} · Skip test: {health.email.today_test_skipped} · Falliti: {health.email.today_failed}
              </div>
            </div>

            {/* Errori client */}
            <div
              className={`p-4 border rounded ${health.errors.total_24h > 20 ? "bg-[#FCE9E4] border-[#F2B6A4]" : health.errors.total_24h > 5 ? "bg-[#FFF4E5] border-[#F2D9B0]" : "bg-white border-[#E5E7E0]"}`}
              data-testid="health-errors"
            >
              <div className="flex items-center justify-between">
                <div className="text-[10.5px] uppercase tracking-[0.2em] font-semibold text-[#6B6A65]">Errori client · 24h</div>
                <BugBeetle size={16} weight="duotone" className="text-[#9A968B]" />
              </div>
              <div className="mt-2 font-editorial tracking-tight text-[#1C1C1A]" style={{ fontSize: 32, lineHeight: 1 }}>
                {health.errors.total_24h}
              </div>
              <div className="text-[11px] text-[#6B6A65] mt-2">
                Top: {health.errors.top.length > 0 ? `${health.errors.top[0].count}×` : "—"}
              </div>
            </div>

            {/* Abuse */}
            <div
              className={`p-4 border rounded ${health.abuse.events_24h > 10 ? "bg-[#FFF4E5] border-[#F2D9B0]" : "bg-white border-[#E5E7E0]"}`}
              data-testid="health-abuse"
            >
              <div className="flex items-center justify-between">
                <div className="text-[10.5px] uppercase tracking-[0.2em] font-semibold text-[#6B6A65]">Soft-ban · 24h</div>
                <Warning size={16} weight="duotone" className="text-[#9A968B]" />
              </div>
              <div className="mt-2 font-editorial tracking-tight text-[#1C1C1A]" style={{ fontSize: 32, lineHeight: 1 }}>
                {health.abuse.events_24h}
              </div>
              <div className="text-[11px] text-[#6B6A65] mt-2">
                Utenti flaggati: {health.abuse.flagged_users_total}
              </div>
            </div>

            {/* Signups today */}
            <div className="p-4 border bg-[#F1F4ED] border-[#7C8B6E]/30 rounded" data-testid="health-signups">
              <div className="flex items-center justify-between">
                <div className="text-[10.5px] uppercase tracking-[0.2em] font-semibold text-[#3F4A33]">Iscrizioni · oggi</div>
                <Users size={16} weight="duotone" className="text-[#7C8B6E]" />
              </div>
              <div className="mt-2 font-editorial tracking-tight text-[#1C1C1A]" style={{ fontSize: 32, lineHeight: 1 }}>
                {health.signups.today}
              </div>
              <div className="text-[11px] text-[#3F4A33] mt-2">
                Mongo: <strong>{health.services.mongo}</strong>
              </div>
            </div>
          </div>

          {/* Riga 2: dettaglio email + errori */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* Email by kind today */}
            <div className="bg-white border border-[#E5E7E0] p-4 rounded" data-testid="health-email-detail">
              <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold mb-3">
                <Pulse size={13} /> Email reali inviate oggi · per categoria
              </div>
              {health.email.by_kind_today.length === 0 ? (
                <div className="text-[12px] text-[#9A968B] italic">Nessuna email reale inviata oggi.</div>
              ) : (
                <div className="space-y-1.5">
                  {health.email.by_kind_today.map((row) => (
                    <div key={row.kind} className="flex items-center gap-3 text-[12.5px]">
                      <span className="flex-1 text-[#1C1C1A]">{KIND_LABELS[row.kind] || row.kind}</span>
                      <span className="font-mono font-semibold text-[#7C8B6E]">{row.count}</span>
                    </div>
                  ))}
                </div>
              )}
              {health.email.recent.length > 0 && (
                <details className="mt-4">
                  <summary className="text-[10.5px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold cursor-pointer hover:text-[#1C1C1A]">
                    Ultimi {health.email.recent.length} invii reali
                  </summary>
                  <div className="mt-2 space-y-1 max-h-48 overflow-y-auto" data-testid="health-email-recent">
                    {health.email.recent.map((r, i) => (
                      <div key={i} className="flex items-center gap-2 text-[11px] py-1 border-b border-[#F0EFE9] last:border-0">
                        <span className="text-[#9A968B] whitespace-nowrap">{(r.created_at || "").slice(5, 16).replace("T", " ")}</span>
                        <span className="flex-1 truncate text-[#1C1C1A]">{r.to}</span>
                        <span className="text-[10px] px-1.5 py-0.5 bg-[#F1F4ED] text-[#7C8B6E] rounded whitespace-nowrap">{r.kind || "—"}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>

            {/* Top errori */}
            <div className="bg-white border border-[#E5E7E0] p-4 rounded" data-testid="health-top-errors">
              <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold mb-3">
                <BugBeetle size={13} /> Top errori client · 24h
              </div>
              {health.errors.top.length === 0 ? (
                <div className="text-[12px] text-[#9A968B] italic">Zero errori segnalati. Stato pulito.</div>
              ) : (
                <div className="space-y-2">
                  {health.errors.top.map((e, i) => (
                    <div key={i} className="text-[11.5px] py-1.5 border-b border-[#F0EFE9] last:border-0">
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-[#1C1C1A] flex-1 break-words font-mono text-[10.5px]">{e.message}</span>
                        <span className="font-mono font-bold text-[#C66060] whitespace-nowrap">{e.count}×</span>
                      </div>
                      {e.last_path && <div className="text-[10px] text-[#9A968B] mt-0.5 truncate">{e.last_path}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <p className="text-[10.5px] text-[#9A968B] mt-3">
            Aggiornato: {(health.generated_at || "").slice(0, 19).replace("T", " ")} UTC · Quota Resend gratis: {health.email.daily_quota} email/giorno
          </p>

          {/* iter v27 r5 — Anteprima email: il founder può aprire ogni template
               in browser per controllare logo, font, testo, bottone CTA prima
               che le email partano a clienti veri. */}
          <div className="mt-6 bg-white border border-[#E5E7E0] p-4 rounded" data-testid="email-previews">
            <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold mb-1">
              <EnvelopeSimple size={13} /> Anteprima email · controlla qui prima che parta a un cliente
            </div>
            <p className="text-[11.5px] text-[#9A968B] mb-3">
              Apri ognuna in un nuovo tab per vedere come la vedrà il destinatario (logo, font, layout).
            </p>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
              {[
                { kind: "verification", label: "Conferma email · nuovo agente" },
                { kind: "lead_notification", label: "Notifica lead → agente" },
                { kind: "lead_auto_reply", label: "Auto-reply → cliente con 3 orari" },
                { kind: "viewing_confirmation", label: "Conferma sopralluogo → cliente" },
                { kind: "password_reset", label: "Reset password" },
                { kind: "marketplace_new_listing", label: "Marketplace · nuova casa" },
              ].map((e) => (
                <button
                  key={e.kind}
                  type="button"
                  onClick={async () => {
                    try {
                      const { data } = await api.get(`/admin/email-preview/${e.kind}`, { responseType: "text" });
                      const w = window.open("", "_blank");
                      if (w) { w.document.open(); w.document.write(data); w.document.close(); }
                    } catch (err) {
                      console.error(err);
                    }
                  }}
                  className="text-left px-3 py-2 border border-[#E5E7E0] rounded text-[12px] text-[#1C1C1A] hover:bg-[#F1F4ED] hover:border-[#7C8B6E] transition-colors"
                  data-testid={`email-preview-${e.kind}`}
                >
                  {e.label} <span className="text-[#9A968B]">→</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Top stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <StatCard label="Iscritti totali" value={s.total_users || 0} sub={`${s.users_today || 0} oggi · ${s.users_7d || 0} settimana`} icon={Users} accent testid="stat-users-total" />
        <StatCard label="Email verificate" value={`${s.verification_rate || 0}%`} sub={`${s.users_verified || 0} su ${s.total_users || 0}`} icon={CheckCircle} testid="stat-verified" />
        <StatCard label="Immobili creati" value={s.total_properties || 0} sub={`${s.props_7d || 0} ultima settimana`} icon={House} testid="stat-properties" />
        <StatCard label="Foto caricate" value={s.total_photos || 0} sub="totale storico" icon={ImageIcon} testid="stat-photos" />
      </div>

      {/* iter v28 r466 — User M1389: «counter Universal Key quando devo
          caricarla». Pannello dedicato che mostra spesa today/7d/30d/total,
          % consumata del budget e alert "Carica la key" se >75%. */}
      {data?.universal_key && (() => {
        const uk = data.universal_key;
        const usagePct = uk.monthly_usage_pct || 0;
        const barColor = usagePct >= 75 ? "bg-red-500" : usagePct >= 50 ? "bg-amber-500" : "bg-[#7C8B6E]";
        return (
          <div className="bg-white border border-[#E5E7E0] p-5 mb-8" data-testid="universal-key-panel">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold">
                  <Sparkle size={14} weight="fill" className="text-[#C8A66B]" /> Universal Key · consumo
                </div>
                <div className="text-[11px] text-[#9A968B] mt-1">Spesa AI reale sulla Emergent LLM Key · €{uk.price_per_credit_eur}/credito</div>
              </div>
              {uk.should_topup && (
                <div className="px-3 py-1 bg-red-100 text-red-700 text-[11px] font-bold uppercase tracking-wide rounded-full" data-testid="uk-topup-alert">
                  ⚠️ Carica la key
                </div>
              )}
            </div>

            {/* Progress bar mensile */}
            <div className="mb-3">
              <div className="flex justify-between items-baseline text-[11px] text-[#6B6A65] mb-1.5">
                <span>Ultimi 30 giorni</span>
                <span><span className="font-semibold text-[#1C1C1A]">€{uk.cost_30d_eur}</span> / €{uk.monthly_budget_eur} ({usagePct}%)</span>
              </div>
              <div className="w-full h-2 bg-[#F1EFE8] rounded-full overflow-hidden">
                <div className={`h-full ${barColor} transition-all`} style={{ width: `${Math.min(100, usagePct)}%` }} />
              </div>
            </div>

            {/* 4 sub-stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              <div className="bg-[#FAF8F2] border border-[#EAE7DF] rounded-md px-3 py-2">
                <div className="text-[9px] uppercase tracking-[0.16em] text-[#9A968B] font-semibold">Oggi</div>
                <div className="font-editorial text-xl text-[#1C1C1A] leading-tight mt-0.5">€{uk.cost_today_eur}</div>
                <div className="text-[10px] text-[#6B6A65]">{uk.credits_today} crediti</div>
              </div>
              <div className="bg-[#FAF8F2] border border-[#EAE7DF] rounded-md px-3 py-2">
                <div className="text-[9px] uppercase tracking-[0.16em] text-[#9A968B] font-semibold">Settimana</div>
                <div className="font-editorial text-xl text-[#1C1C1A] leading-tight mt-0.5">€{uk.cost_7d_eur}</div>
                <div className="text-[10px] text-[#6B6A65]">{uk.credits_7d} crediti</div>
              </div>
              <div className="bg-[#FAF8F2] border border-[#EAE7DF] rounded-md px-3 py-2">
                <div className="text-[9px] uppercase tracking-[0.16em] text-[#9A968B] font-semibold">Mese</div>
                <div className="font-editorial text-xl text-[#7C8B6E] leading-tight mt-0.5">€{uk.cost_30d_eur}</div>
                <div className="text-[10px] text-[#6B6A65]">{uk.credits_30d} crediti</div>
              </div>
              <div className="bg-[#FAF8F2] border border-[#EAE7DF] rounded-md px-3 py-2">
                <div className="text-[9px] uppercase tracking-[0.16em] text-[#9A968B] font-semibold">Totale</div>
                <div className="font-editorial text-xl text-[#1C1C1A] leading-tight mt-0.5">€{uk.cost_all_time_eur}</div>
                <div className="text-[10px] text-[#6B6A65]">{uk.credits_all_time} crediti</div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Extension health · iter 305 round 6 */}
      <ExtensionHealthPanel />

      {/* Trend chart */}
      <div className="bg-white border border-[#E5E7E0] p-5 mb-8" data-testid="trend-chart">        <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.2em] text-[#6B6A65] mb-3 font-semibold">
          <TrendUp size={14} /> Iscrizioni · ultimi 14 giorni
        </div>
        <Sparkline trend={data?.daily_trend || []} />
        <div className="flex justify-between text-[10px] text-[#9A968B] mt-2">
          {data?.daily_trend?.length > 0 && (
            <>
              <span>{data.daily_trend[0]?.date.slice(5)}</span>
              <span>{data.daily_trend[data.daily_trend.length - 1]?.date.slice(5)}</span>
            </>
          )}
        </div>
      </div>

      {/* Top active */}
      {(data?.top_active || []).length > 0 && (
        <div className="mb-8">
          <h2 className="text-[11px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold mb-3">Top agenti attivi · ultimi 30gg</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="top-active-list">
            {data.top_active.map((u, i) => (
              <div key={u.user_id} className="bg-white border border-[#E5E7E0] p-3 flex items-center gap-3" data-testid={`top-user-${i}`}>
                <span className="font-editorial text-2xl text-[#C8A66B] tracking-tight w-6">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-[#1C1C1A] truncate">{u.name || u.email.split("@")[0]}</div>
                  <div className="text-[10.5px] text-[#9A968B] truncate">{u.email}</div>
                </div>
                <div className="text-right">
                  <div className="font-editorial text-2xl text-[#7C8B6E] tracking-tight leading-none">{u.properties_count}</div>
                  <div className="text-[9px] uppercase tracking-[0.16em] text-[#9A968B]">immobili</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent users table */}
      <div>
        <h2 className="text-[11px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold mb-3">
          Ultimi iscritti · {sortedUsers.length}
        </h2>
        <div className="bg-white border border-[#E5E7E0] overflow-x-auto" data-testid="recent-users-table">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F4EE] text-[10px] uppercase tracking-[0.16em] text-[#6B6A65]">
              <tr>
                <th className="px-3 py-2.5 text-left font-semibold">Quando</th>
                <th className="px-3 py-2.5 text-left font-semibold">Agente</th>
                <th className="px-3 py-2.5 text-left font-semibold">Email</th>
                <th className="px-3 py-2.5 text-left font-semibold">Agenzia</th>
                <th className="px-3 py-2.5 text-center font-semibold">Verif.</th>
                <th className="px-3 py-2.5 text-right font-semibold">Imm.</th>
                <th className="px-3 py-2.5 text-right font-semibold">Foto</th>
                <th className="px-3 py-2.5 text-right font-semibold">Pubbl.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F0EFE9]">
              {sortedUsers.map((u) => (
                <tr key={u.user_id} className={u.is_active ? "bg-[#FAFAF7]" : ""} data-testid={`user-${u.user_id}`}>
                  <td className="px-3 py-2.5 text-[11.5px] text-[#9A968B]">{(u.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-3 py-2.5 font-semibold text-[#1C1C1A]">{u.name || "—"}</td>
                  <td className="px-3 py-2.5 text-[12.5px] text-[#6B6A65]">{u.email}</td>
                  <td className="px-3 py-2.5 text-[12.5px] text-[#6B6A65]">
                    {u.agency_name || <span className="italic text-[#C5C2B8]">—</span>}
                    {u.agency_phone && <div className="text-[10.5px] text-[#9A968B]">{u.agency_phone}</div>}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    {u.email_verified ? <CheckCircle size={15} weight="fill" className="text-[#5C8262] inline" /> : <span className="text-[#C66060] text-[10px]">no</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right font-semibold">{u.properties_count}</td>
                  <td className="px-3 py-2.5 text-right text-[#7C8B6E]">{u.photos_count}</td>
                  <td className="px-3 py-2.5 text-right text-[#C8A66B] font-semibold">{u.published_count}</td>
                </tr>
              ))}
              {sortedUsers.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-[#9A968B]">Nessun iscritto ancora.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* iter v28 r404 — Le sezioni Esporta/DAU/Errori sono state spostate in cima */}

      {/* Landing chip analytics — first-click signal */}
      <LandingChipAnalytics />

      <p className="mt-6 text-[10.5px] text-[#9A968B] italic text-right">
        Dati aggiornati: {data?.generated_at?.slice(0, 19).replace("T", " ")}
      </p>
    </AppShell>
  );
}

// iter v28 r404 — Banner "Esporta snapshot" — testo Markdown copiabile
// con tutto il contesto utile (DAU, errori, signups) per condividerlo in
// chat con il developer. User: «posso inviare e mandare a te per
// implementare e risolvere».
function ExportSnapshotBanner() {
  const [days, setDays] = useState(7);
  const [busy, setBusy] = useState(false);

  const copyToClipboard = async () => {
    setBusy(true);
    try {
      const { toast } = await import("sonner");
      const { data } = await api.get(`/admin/export-snapshot?days=${days}`);
      const text = data?.text || "";
      await navigator.clipboard.writeText(text);
      toast.success(`Snapshot copiato (${text.length} caratteri). Incollalo in chat.`);
    } catch {
      const { toast } = await import("sonner");
      toast.error("Impossibile copiare. Riprova.");
    } finally {
      setBusy(false);
    }
  };

  const downloadFile = async () => {
    setBusy(true);
    try {
      const { toast } = await import("sonner");
      const { data } = await api.get(`/admin/export-snapshot?days=${days}`);
      const blob = new Blob([data?.text || ""], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pronto-snapshot-${new Date().toISOString().slice(0,10)}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Snapshot scaricato.");
    } catch {
      const { toast } = await import("sonner");
      toast.error("Download fallito.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-10 p-5 bg-[#1C1C1A] text-white rounded-lg shadow-lg" data-testid="export-snapshot-banner">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="text-[10.5px] uppercase tracking-[0.2em] text-[#C8A66B] font-semibold mb-1">
            Manda tutto al dev
          </div>
          <h2 className="font-editorial text-2xl tracking-tight mb-2">Esporta snapshot</h2>
          <p className="text-[13px] text-[#C5C2B8] max-w-2xl leading-snug">
            Genera un riepilogo testuale di tutto: utenti attivi, errori server, errori frontend, iscrizioni, attività. Copialo o scaricalo e incollalo nella chat — il dev sa cosa fare.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {[1, 7, 30].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 rounded-full text-[11.5px] font-semibold transition-colors ${
                days === d ? "bg-[#C8A66B] text-[#1C1C1A]" : "bg-white/10 text-white/80 hover:bg-white/15"
              }`}
              data-testid={`export-days-${d}`}
            >
              {d === 1 ? "24h" : `${d}g`}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        <button
          type="button"
          onClick={copyToClipboard}
          disabled={busy}
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#C8A66B] hover:bg-[#B89556] text-[#1C1C1A] text-[13px] font-semibold rounded transition-colors disabled:opacity-50"
          data-testid="export-copy-btn"
        >
          {busy ? "Genero…" : "Copia snapshot per dev"}
        </button>
        <button
          type="button"
          onClick={downloadFile}
          disabled={busy}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-white/15 text-white text-[13px] font-semibold rounded transition-colors disabled:opacity-50"
          data-testid="export-download-btn"
        >
          Scarica .txt
        </button>
      </div>
    </div>
  );
}

// iter v28 r465 — Ricarica crediti utente per email (founder-only).
// Usato per topup manuali su utenti specifici (recovery, supporto, premio test).
function GrantCreditsPanel() {
  const [email, setEmail] = useState("");
  const [amount, setAmount] = useState(50);
  const [resetDaily, setResetDaily] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || amount <= 0) return;
    setBusy(true);
    setResult(null);
    try {
      const { toast } = await import("sonner");
      const { data } = await api.post("/admin/credits/grant-by-email", {
        email: email.trim(),
        amount: Number(amount),
        reason: "founder_topup_admin_panel",
        reset_daily_cap: resetDaily,
      });
      setResult(data);
      toast.success(`+${data.granted} crediti a ${data.user?.email}`);
    } catch (err) {
      const { toast } = await import("sonner");
      const msg = err?.response?.data?.detail || err?.message || "Errore";
      toast.error(msg);
      setResult({ error: msg });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 mb-10 p-5 bg-white border border-[#E5E7E0] rounded-lg" data-testid="grant-credits-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.2em] text-[#7C8B6E] font-semibold mb-1">
            Supporto utenti
          </div>
          <h2 className="font-editorial text-2xl tracking-tight text-[#1C1C1A]">Ricarica crediti per email</h2>
          <p className="text-[12.5px] text-[#6B6A65] mt-1 max-w-xl leading-snug">
            Cerca un utente per email e aggiungi crediti al suo bucket top-up (non scadono). Utile per recovery, supporto, premi e omaggi.
          </p>
        </div>
      </div>
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-[1fr_120px_auto] gap-2 items-end">
        <div>
          <label className="text-[10.5px] uppercase tracking-[0.16em] text-[#9A968B] font-semibold mb-1 block">Email utente</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="es. dottfabioarioli@gmail.com"
            className="w-full px-3 py-2.5 border border-[#E5E7E0] rounded text-[14px] focus:outline-none focus:border-[#7C8B6E]"
            data-testid="grant-email-input"
            required
          />
        </div>
        <div>
          <label className="text-[10.5px] uppercase tracking-[0.16em] text-[#9A968B] font-semibold mb-1 block">Crediti</label>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            min="1"
            max="10000"
            className="w-full px-3 py-2.5 border border-[#E5E7E0] rounded text-[14px] focus:outline-none focus:border-[#7C8B6E]"
            data-testid="grant-amount-input"
            required
          />
        </div>
        <button
          type="submit"
          disabled={busy}
          className="px-5 py-2.5 bg-[#1C1C1A] text-white text-[13px] font-semibold rounded hover:bg-[#2A2A28] transition-colors disabled:opacity-50"
          data-testid="grant-submit-btn"
        >
          {busy ? "Ricarico…" : "Ricarica"}
        </button>
      </form>
      <label className="flex items-center gap-2 mt-3 text-[12.5px] text-[#6B6A65]">
        <input
          type="checkbox"
          checked={resetDaily}
          onChange={(e) => setResetDaily(e.target.checked)}
          data-testid="grant-reset-daily"
        />
        Azzera anche il cap giornaliero gratuito (sblocca subito)
      </label>

      {result && !result.error && (
        <div className="mt-4 p-3 bg-[#EFF4E9] border border-[#CBD9B8] rounded text-[12.5px] text-[#3F4A33]" data-testid="grant-result">
          <strong>✓ Ricaricati {result.granted} crediti</strong> a {result.user?.email}
          {result.user?.name && ` (${result.user.name})`}
          {result.user?.agency_name && ` · ${result.user.agency_name}`}
          <br />
          Nuovo saldo top-up: <strong>{result.new_topup_balance}</strong>
          {result.daily_cap_reset && " · Cap giornaliero azzerato"}
        </div>
      )}
      {result?.error && (
        <div className="mt-4 p-3 bg-[#FFF4E5] border border-[#F2D9B0] rounded text-[12.5px] text-[#7A4E0E]">
          ⚠ {result.error}
        </div>
      )}
    </div>
  );
}

// iter v28 r404 — DAU = utenti che hanno fatto almeno un'azione
// (creato property/foto/contatto/listing/agenda) quel giorno.
// Diverso dai signups: questo è chi USA davvero l'app.
function DauPanel() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(14);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/dau?days=${days}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [days]);

  const trend = data?.trend || [];
  const summary = data?.summary || {};
  const maxCount = Math.max(...trend.map((t) => t.active_users), 1);

  return (
    <div className="mt-10" data-testid="dau-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-[11px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold inline-flex items-center gap-2">
          <Pulse size={13} weight="duotone" />
          Utenti attivi al giorno · DAU
        </h2>
        <div className="flex items-center gap-1.5">
          {[7, 14, 30].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                days === d ? "bg-[#1C1C1A] text-white" : "bg-white border border-[#E5E7E0] text-[#6B6A65] hover:border-[#7C8B6E]"
              }`}
              data-testid={`dau-days-${d}`}
            >
              {d}g
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="bg-white border border-[#E5E7E0] p-8 text-center text-[#9A968B] text-sm">Carico…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-3">
          {/* KPI sintetici */}
          <div className="bg-white border border-[#E5E7E0] p-4 rounded">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10.5px] uppercase tracking-[0.16em] text-[#9A968B] font-semibold">Oggi</div>
                <div className="font-editorial text-3xl text-[#1C1C1A] tracking-tight leading-none mt-1">{summary.today ?? "—"}</div>
              </div>
              <div>
                <div className="text-[10.5px] uppercase tracking-[0.16em] text-[#9A968B] font-semibold">Ieri</div>
                <div className="font-editorial text-3xl text-[#6B6A65] tracking-tight leading-none mt-1">{summary.yesterday ?? "—"}</div>
              </div>
              <div className="col-span-2 pt-3 border-t border-[#F0EFE9]">
                <div className="text-[10.5px] uppercase tracking-[0.16em] text-[#9A968B] font-semibold">Media 7gg · picco {days}g</div>
                <div className="flex items-baseline gap-3 mt-1">
                  <span className="font-editorial text-2xl text-[#7C8B6E]">{summary.last_7d_avg ?? "—"}</span>
                  <span className="text-[#9A968B] text-[12px]">picco {summary.peak_in_window ?? "—"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bar chart */}
          <div className="bg-white border border-[#E5E7E0] p-4 rounded">
            <div className="flex items-end gap-1 h-32" data-testid="dau-bars">
              {trend.map((t, i) => {
                const h = Math.max(2, (t.active_users / maxCount) * 100);
                const isToday = i === trend.length - 1;
                return (
                  <div key={t.date} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                    <div className="text-[10px] font-mono font-semibold text-[#1C1C1A]">{t.active_users}</div>
                    <div
                      className="w-full rounded-t"
                      style={{
                        height: `${h}%`,
                        background: isToday ? "#7C8B6E" : "#C8D2BC",
                      }}
                    />
                  </div>
                );
              })}
            </div>
            <div className="mt-2 flex items-center justify-between text-[10px] text-[#9A968B]">
              <span>{trend[0]?.date?.slice(5)}</span>
              <span>oggi</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// iter v28 r404 — Errori server (5xx) — catturati dal middleware
// `capture_server_errors` in server.py. Stessa UI di ClientErrorsPanel
// ma con traceback Python invece di stack JS.
function ServerErrorsPanel() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/server-errors?days=${days}&limit=50`)
      .then((r) => setData(r.data))
      .catch(() => setData({ groups: [], total_events: 0 }))
      .finally(() => setLoading(false));
  }, [days]);

  const groups = data?.groups || [];
  const total = data?.total_events || 0;

  return (
    <div className="mt-10" data-testid="server-errors-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-[11px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold inline-flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full" style={{ background: total > 0 ? "#C66060" : "#5C8262" }} />
          Errori server · backend 5xx
        </h2>
        <div className="flex items-center gap-1.5">
          {[1, 7, 30].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                days === d ? "bg-[#1C1C1A] text-white" : "bg-white border border-[#E5E7E0] text-[#6B6A65] hover:border-[#7C8B6E]"
              }`}
              data-testid={`se-days-${d}`}
            >
              {d === 1 ? "Oggi" : `${d}g`}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="bg-white border border-[#E5E7E0] p-8 text-center text-[#9A968B] text-sm">Carico…</div>
      ) : groups.length === 0 ? (
        <div className="bg-white border border-[#E5E7E0] p-8 text-center text-[#6B6A65]" data-testid="se-empty">
          <div className="text-2xl mb-1">🟢</div>
          <div className="text-[13px] font-semibold text-[#1C1C1A]">Backend pulito</div>
          <div className="text-[11.5px] text-[#9A968B] mt-1">Nessun errore server negli ultimi {days === 1 ? "24h" : `${days} giorni`}.</div>
        </div>
      ) : (
        <>
          <div className="text-[11.5px] text-[#6B6A65] mb-2">
            <strong className="text-[#C66060]">{total}</strong> errori · <strong>{groups.length}</strong> distinti
          </div>
          <div className="bg-white border border-[#E5E7E0] divide-y divide-[#F0EFE9]" data-testid="se-list">
            {groups.map((g, i) => {
              const open = expanded === i;
              return (
                <div key={i} className="px-3 py-2.5" data-testid={`se-row-${i}`}>
                  <div
                    className="flex items-start gap-3 cursor-pointer select-none"
                    onClick={() => setExpanded(open ? null : i)}
                  >
                    <span className="font-mono text-[11px] font-bold tabular-nums text-[#C66060] bg-red-50 px-1.5 py-0.5 rounded shrink-0" style={{ minWidth: 32, textAlign: "center" }}>
                      ×{g.count}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] text-[#1C1C1A] font-mono break-all leading-tight">{g.message}</div>
                      <div className="text-[10.5px] text-[#9A968B] mt-1 flex items-center gap-2 flex-wrap">
                        <span className="bg-[#F1F4ED] text-[#5C6B4E] px-1.5 py-0.5 rounded font-semibold">{g.last_method || "?"} {g.last_status || ""}</span>
                        {g.last_path && <span><strong>{g.last_path}</strong></span>}
                        <span>· {(g.last_seen || "").slice(0, 16).replace("T", " ")}</span>
                      </div>
                    </div>
                    <span className="text-[#9A968B] text-[12px] shrink-0">{open ? "▾" : "▸"}</span>
                  </div>
                  {open && g.last_traceback && (
                    <pre className="mt-2.5 ml-12 bg-[#FAFAF7] border border-[#F0EFE9] text-[10.5px] text-[#3F3D38] p-2 rounded whitespace-pre-wrap break-words overflow-x-auto max-h-60" data-testid={`se-detail-${i}`}>
                      {g.last_traceback}
                    </pre>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function LandingChipAnalytics() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [funnel, setFunnel] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/analytics/landing-chips?days=${days}`)
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    // Funnel signup attribution (iter 240) — independent fetch, non blocca il pannello chips
    api.get(`/admin/analytics/landing-funnel?days=${days}`)
      .then((r) => setFunnel(r.data))
      .catch(() => setFunnel(null));
  }, [days]);

  if (loading || !data) {
    return (
      <div className="bg-white border border-[#E5E7E0] p-5 mb-8" data-testid="chip-analytics-loading">
        <div className="text-[10.5px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold mb-2">
          Landing · chip click analytics
        </div>
        <div className="text-sm text-[#9A968B]">Carico…</div>
      </div>
    );
  }

  const maxFc = Math.max(1, ...(data.first_clicks || []).map((f) => f.first_clicks));
  const LABELS = {
    mandati: "Mandati", marketplace: "Marketplace", foto: "Foto",
    sopralluogo: "Sopralluogo", annuncio: "Annuncio", brochure: "Pianta arredata",
    agenda: "Agenda", stima: "Stima", social: "Social",
  };

  return (
    <div className="bg-white border border-[#E5E7E0] p-5 mb-8" data-testid="chip-analytics">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold mb-1">
            Landing · chip click analytics
          </div>
          <p className="text-[12px] text-[#9A968B] leading-snug max-w-md">
            Quale servizio cattura davvero l'intent. <strong>First-click</strong> = primo chip cliccato in sessione = segnale di interesse reale.
          </p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="text-[11px] border border-[#E5E7E0] rounded px-2 py-1.5 bg-white"
          data-testid="chip-analytics-days"
        >
          <option value={1}>Ultimo giorno</option>
          <option value={7}>Ultimi 7 giorni</option>
          <option value={14}>Ultimi 14 giorni</option>
          <option value={30}>Ultimi 30 giorni</option>
        </select>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <div className="border border-[#E5E7E0] p-3 rounded">
          <div className="text-[9px] uppercase tracking-[0.2em] text-[#9A968B] font-semibold mb-0.5">Sessioni</div>
          <div className="font-editorial text-2xl text-[#1C1C1A]">{data.total_sessions}</div>
        </div>
        <div className="border border-[#E5E7E0] p-3 rounded">
          <div className="text-[9px] uppercase tracking-[0.2em] text-[#9A968B] font-semibold mb-0.5">Engagement</div>
          <div className="font-editorial text-2xl text-[#7C8B6E]">{data.engagement_rate}%</div>
          <div className="text-[10px] text-[#9A968B] mt-0.5">{data.click_sessions} sessioni con click</div>
        </div>
        <div className="border border-[#E5E7E0] p-3 rounded">
          <div className="text-[9px] uppercase tracking-[0.2em] text-[#9A968B] font-semibold mb-0.5">Eventi totali</div>
          <div className="font-editorial text-2xl text-[#1C1C1A]">{data.total_events}</div>
        </div>
        <div className="border border-[#E5E7E0] p-3 rounded">
          <div className="text-[9px] uppercase tracking-[0.2em] text-[#9A968B] font-semibold mb-0.5">Click utente</div>
          <div className="font-editorial text-2xl text-[#1C1C1A]">{data.total_clicks}</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* First-click ranking */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold mb-3">
            🎯 Primo chip cliccato (intent signal)
          </div>
          {(data.first_clicks || []).length === 0 ? (
            <div className="text-[12.5px] text-[#9A968B] italic">Nessun click ancora.</div>
          ) : (
            <div className="space-y-1.5">
              {data.first_clicks.map((fc, idx) => (
                <div key={fc.chip} className="flex items-center gap-2 text-[12.5px]">
                  <span className="text-[10px] text-[#9A968B] w-5 tabular-nums">{idx + 1}.</span>
                  <span className="w-24 font-semibold text-[#1C1C1A]">{LABELS[fc.chip] || fc.chip}</span>
                  <div className="flex-1 bg-[#F1F4ED] rounded-full h-2 overflow-hidden">
                    <div
                      className="h-full bg-[#7C8B6E]"
                      style={{ width: `${(fc.first_clicks / maxFc) * 100}%` }}
                    />
                  </div>
                  <span className="w-10 text-right tabular-nums font-semibold text-[#1C1C1A]">{fc.first_clicks}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* All clicks table */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold mb-3">
            Click totali per chip
          </div>
          {(data.by_chip || []).length === 0 ? (
            <div className="text-[12.5px] text-[#9A968B] italic">Nessuna interazione ancora.</div>
          ) : (
            <table className="w-full text-[12px]">
              <thead className="text-[10px] uppercase tracking-[0.15em] text-[#9A968B] border-b border-[#EAE7DF]">
                <tr>
                  <th className="text-left py-1.5 font-semibold">Chip</th>
                  <th className="text-right py-1.5 font-semibold">Click</th>
                  <th className="text-right py-1.5 font-semibold">Sessioni</th>
                  <th className="text-right py-1.5 font-semibold">Autoplay views</th>
                </tr>
              </thead>
              <tbody>
                {data.by_chip.map((r) => (
                  <tr key={r.chip} className="border-b border-[#F4F2EC]">
                    <td className="py-1.5 font-semibold text-[#1C1C1A]">{LABELS[r.chip] || r.chip}</td>
                    <td className="py-1.5 text-right tabular-nums">{r.click_events}</td>
                    <td className="py-1.5 text-right tabular-nums">{r.click_sessions}</td>
                    <td className="py-1.5 text-right tabular-nums text-[#9A968B]">{r.autoplay_events}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── FUNNEL CHIP → SIGNUP (iter 240) ────────────────────────── */}
      {funnel && (
        <div className="mt-6 pt-6 border-t border-[#EAE7DF]">
          <div className="flex items-baseline justify-between mb-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold">
              Funnel · chip → iscrizione
            </div>
            <div className="text-[11px] text-[#9A968B]">
              <strong className="text-[#1C1C1A]">{funnel.total_signups}</strong> iscritti totali ·
              <span className="ml-1">{funnel.attributed_signups} attribuiti, {funnel.unattributed_signups} senza tracciamento</span>
            </div>
          </div>
          {(funnel.funnel || []).length === 0 ? (
            <div className="text-[12.5px] text-[#9A968B] italic">Nessuna iscrizione tracciata ancora.</div>
          ) : (
            <table className="w-full text-[12px]" data-testid="funnel-table">
              <thead className="text-[10px] uppercase tracking-[0.15em] text-[#9A968B] border-b border-[#EAE7DF]">
                <tr>
                  <th className="text-left py-1.5 font-semibold">Primo chip cliccato</th>
                  <th className="text-right py-1.5 font-semibold">Sessioni</th>
                  <th className="text-right py-1.5 font-semibold">Iscritti</th>
                  <th className="text-right py-1.5 font-semibold">Conv %</th>
                </tr>
              </thead>
              <tbody>
                {funnel.funnel.map((r) => (
                  <tr key={r.chip} className="border-b border-[#F4F2EC]">
                    <td className="py-1.5 font-semibold text-[#1C1C1A]">{LABELS[r.chip] || r.chip}</td>
                    <td className="py-1.5 text-right tabular-nums text-[#6B6A65]">{r.first_click_sessions}</td>
                    <td className="py-1.5 text-right tabular-nums font-semibold text-[#1C1C1A]">{r.signups}</td>
                    <td className={`py-1.5 text-right tabular-nums font-semibold ${r.conversion_rate > 0 ? "text-[#5C6A4F]" : "text-[#C5C2B8]"}`}>
                      {r.conversion_rate != null ? `${r.conversion_rate}%` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="mt-2 text-[10.5px] text-[#9A968B] italic">
            Conversione = % di sessioni che hanno cliccato per primo questo chip e si sono iscritte.
          </div>
        </div>
      )}
    </div>
  );
}


function ClientErrorsPanel() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/client-errors?days=${days}&limit=50`)
      .then((r) => setData(r.data))
      .catch(() => setData({ groups: [], total_events: 0 }))
      .finally(() => setLoading(false));
  }, [days]);

  const groups = data?.groups || [];
  const total = data?.total_events || 0;

  const sendTestDigest = async () => {
    try {
      const { toast } = await import("sonner");
      await api.post("/push/agenda/trigger/founder_errors");
      toast.success("Digest inviato (controlla notifiche del browser/telefono)");
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error("Invio digest fallito");
    }
  };

  return (
    <div className="mt-10" data-testid="client-errors-panel" id="client-errors-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-[11px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold inline-flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full" style={{ background: total > 0 ? "#C66060" : "#5C8262" }} />
          Errori utenti · early warning
        </h2>
        <div className="flex items-center gap-1.5">
          <button
            onClick={sendTestDigest}
            className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white border border-[#E5E7E0] text-[#6B6A65] hover:border-[#7C8B6E] transition-colors"
            title="Manda subito il digest push al founder per validare push subscription"
            data-testid="ce-test-digest"
          >
            Invia digest test
          </button>
          {[1, 7, 30].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                days === d ? "bg-[#1C1C1A] text-white" : "bg-white border border-[#E5E7E0] text-[#6B6A65] hover:border-[#7C8B6E]"
              }`}
              data-testid={`ce-days-${d}`}
            >
              {d === 1 ? "Oggi" : `${d}g`}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="bg-white border border-[#E5E7E0] p-8 text-center text-[#9A968B] text-sm">Carico…</div>
      ) : groups.length === 0 ? (
        <div className="bg-white border border-[#E5E7E0] p-8 text-center text-[#6B6A65]" data-testid="ce-empty">
          <div className="text-2xl mb-1">🟢</div>
          <div className="text-[13px] font-semibold text-[#1C1C1A]">Tutto liscio</div>
          <div className="text-[11.5px] text-[#9A968B] mt-1">Nessun errore client negli ultimi {days === 1 ? "24h" : `${days} giorni`}.</div>
        </div>
      ) : (
        <>
          <div className="text-[11.5px] text-[#6B6A65] mb-2">
            <strong className="text-[#C66060]">{total}</strong> eventi · <strong>{groups.length}</strong> errori distinti
          </div>
          <div className="bg-white border border-[#E5E7E0] divide-y divide-[#F0EFE9]" data-testid="ce-list">
            {groups.map((g, i) => {
              const open = expanded === i;
              return (
                <div key={i} className="px-3 py-2.5" data-testid={`ce-row-${i}`}>
                  <div
                    className="flex items-start gap-3 cursor-pointer select-none"
                    onClick={() => setExpanded(open ? null : i)}
                  >
                    <span className="font-mono text-[11px] font-bold tabular-nums text-[#C66060] bg-red-50 px-1.5 py-0.5 rounded shrink-0" style={{ minWidth: 32, textAlign: "center" }}>
                      ×{g.count}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] text-[#1C1C1A] font-mono break-all leading-tight">{g.message}</div>
                      <div className="text-[10.5px] text-[#9A968B] mt-1 flex items-center gap-2 flex-wrap">
                        {g.last_path && <span><strong>{g.last_path}</strong></span>}
                        <span>· ultimo {(g.last_seen || "").slice(0, 16).replace("T", " ")}</span>
                        {g.last_user && <span>· user <code className="bg-[#F1F4ED] px-1 rounded">{(g.last_user || "").slice(0, 14)}</code></span>}
                      </div>
                    </div>
                    <span className="text-[#9A968B] text-[12px] shrink-0">{open ? "▾" : "▸"}</span>
                  </div>
                  {open && (
                    <div className="mt-2.5 pl-12 space-y-1.5" data-testid={`ce-detail-${i}`}>
                      {g.last_stack && (
                        <pre className="bg-[#FAFAF7] border border-[#F0EFE9] text-[10.5px] text-[#3F3D38] p-2 rounded whitespace-pre-wrap break-words overflow-x-auto max-h-40">
                          {g.last_stack}
                        </pre>
                      )}
                      <div className="text-[10.5px] text-[#9A968B] grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5">
                        <span><strong>Primo visto:</strong> {(g.first_seen || "").slice(0, 16).replace("T", " ")}</span>
                        <span><strong>Ultimo:</strong> {(g.last_seen || "").slice(0, 16).replace("T", " ")}</span>
                        {g.last_ua && <span className="sm:col-span-2 truncate" title={g.last_ua}><strong>UA:</strong> {g.last_ua}</span>}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
